import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CreateKeyImageError,
  CreateKeyImageRequest,
  CreateKeyImageResponse,
  KeyImageRenderedSummaryError,
  KeyImageRenderedSummaryRequest,
  KeyImageRenderedSummaryResponse,
  PatchKeyImageError,
  PatchKeyImageRequest,
  PatchKeyImageResponse,
} from '@/api/model';

const KEY = {
  GET_KEY_IMAGE_RENDERED_SUMMARY: 'GET_KEY_IMAGE_RENDERED_SUMMARY',
} as const;

const request = {
  getKeyImageRenderedSummary: async (
    { payload: { renderedSummaryUuid } }: KeyImageRenderedSummaryRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<KeyImageRenderedSummaryResponse>(
      `${ENDPOINTS.FCT.KEY_IMAGE.RENDERED_SUMMARY}${renderedSummaryUuid}`,
      { signal },
    );

    return data;
  },
  patchKeyImage: async ({ payload }: PatchKeyImageRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchKeyImageResponse>(
      `${ENDPOINTS.FCT.KEY_IMAGE.BASE}${payload.uuid}`,
      { body: payload },
    );

    return data;
  },
  postCreateKeyImage: async ({ payload }: CreateKeyImageRequest) => {
    const { data } = await fetchClient.FCT.post<CreateKeyImageResponse>(
      ENDPOINTS.FCT.KEY_IMAGE.BASE,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetKeyImageRenderedSummary: (
    { payload }: KeyImageRenderedSummaryRequest,
    config?: Partial<
      UseQueryOptions<KeyImageRenderedSummaryResponse, KeyImageRenderedSummaryError>
    >,
  ) =>
    useQuery<KeyImageRenderedSummaryResponse, KeyImageRenderedSummaryError>({
      queryFn: ({ signal }) => request.getKeyImageRenderedSummary({ payload }, signal),
      queryKey: [KEY.GET_KEY_IMAGE_RENDERED_SUMMARY, payload.renderedSummaryUuid],
      ...config,
    }),
};

const mutation = {
  usePatchKeyImage: () =>
    useMutation<PatchKeyImageResponse, PatchKeyImageError, PatchKeyImageRequest>({
      mutationFn: request.patchKeyImage,
    }),
  usePostCreateKeyImage: () =>
    useMutation<CreateKeyImageResponse, CreateKeyImageError, CreateKeyImageRequest>({
      mutationFn: request.postCreateKeyImage,
    }),
};

export const keyImage = {
  KEY,
  mutation,
  query,
  request,
};
